interface CylinderOptions {
    radius: number;
    height: number;
    fillColor: string;
    unit?: string;
}

export default function drawCylinder(canvas: HTMLCanvasElement, opts: CylinderOptions): void {
    const W = 460;
    const H = 400;
    canvas.width = W;
    canvas.height = H;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { radius, height, fillColor, unit = '' } = opts;
    const fmt = (n: number) => parseFloat(n.toFixed(4)).toString();

    const MAX_R = 15;
    const MAX_H = 20;
    const MAX_R_PX = 110;
    const MAX_H_PX = 200;

    const rPx = Math.min(MAX_R_PX, Math.max(18, (radius / MAX_R) * MAX_R_PX));
    const hPx = Math.min(MAX_H_PX, Math.max(24, (height / MAX_H) * MAX_H_PX));
    const eH = rPx * 0.3;

    // Center the cylinder slightly left to leave room for the height label
    const cx = W / 2 - 10;
    const cy = H / 2;
    const topY = cy - hPx / 2;
    const botY = cy + hPx / 2;

    ctx.clearRect(0, 0, W, H);

    // Body gradient: darker on left edge, highlight in center, slightly dark on right
    const grad = ctx.createLinearGradient(cx - rPx, cy, cx + rPx, cy);
    grad.addColorStop(0, shade(fillColor, 0.45));
    grad.addColorStop(0.38, shade(fillColor, 0.88));
    grad.addColorStop(0.6, fillColor);
    grad.addColorStop(1, shade(fillColor, 0.7));

    // --- Cylinder body fill ---
    // Path: right top → right side → front-bottom arc → left side → back-top arc → close
    ctx.beginPath();
    ctx.moveTo(cx + rPx, topY);
    ctx.lineTo(cx + rPx, botY);
    // front (visible) half of bottom ellipse: clockwise from 0 to PI
    ctx.ellipse(cx, botY, rPx, eH, 0, 0, Math.PI);
    ctx.lineTo(cx - rPx, topY);
    // back (hidden) half of top ellipse: clockwise from PI to 2PI
    ctx.ellipse(cx, topY, rPx, eH, 0, Math.PI, 0);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();

    // --- Back arc of bottom ellipse (dashed, hidden behind body) ---
    ctx.beginPath();
    ctx.ellipse(cx, botY, rPx, eH, 0, Math.PI, 2 * Math.PI);
    ctx.setLineDash([3, 4]);
    ctx.strokeStyle = 'rgba(0,0,0,0.18)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.setLineDash([]);

    // --- Front arc of bottom ellipse (solid) ---
    ctx.beginPath();
    ctx.ellipse(cx, botY, rPx, eH, 0, 0, Math.PI);
    ctx.strokeStyle = 'rgba(0,0,0,0.3)';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // --- Top ellipse (lighter fill + outline) ---
    ctx.beginPath();
    ctx.ellipse(cx, topY, rPx, eH, 0, 0, Math.PI * 2);
    ctx.fillStyle = shade(fillColor, 1.25);
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.2)';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // --- Side outline edges ---
    ctx.strokeStyle = 'rgba(0,0,0,0.2)';
    ctx.lineWidth = 1;

    ctx.beginPath();
    ctx.moveTo(cx - rPx, topY);
    ctx.lineTo(cx - rPx, botY);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(cx + rPx, topY);
    ctx.lineTo(cx + rPx, botY);
    ctx.stroke();

    // ---- DIMENSION LABELS ----
    const blue = '#185FA5';
    const textColor = '#1a1a18';

    // Radius label: dashed line from center to LEFT edge of top ellipse, label to the left
    const rEndX = cx - rPx;
    const rY = topY;

    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = blue;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(cx, rY);
    ctx.lineTo(rEndX, rY);
    ctx.stroke();
    ctx.setLineDash([]);

    arrowHead(ctx, cx, rY, rEndX, rY, blue, 7);

    ctx.font = '500 13px system-ui, sans-serif';
    ctx.fillStyle = textColor;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.fillText(`r = ${fmt(radius)}${unit ? ' ' + unit : ''}`, rEndX - 6, rY - 4);

    // Height label: dashed ticks + vertical arrow on the right
    const hLineX = cx + rPx + 44;

    ctx.setLineDash([3, 3]);
    ctx.strokeStyle = blue;
    ctx.lineWidth = 1.5;

    ctx.beginPath();
    ctx.moveTo(cx + rPx + 6, topY);
    ctx.lineTo(hLineX, topY);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(cx + rPx + 6, botY);
    ctx.lineTo(hLineX, botY);
    ctx.stroke();

    ctx.setLineDash([]);

    ctx.beginPath();
    ctx.moveTo(hLineX, topY);
    ctx.lineTo(hLineX, botY);
    ctx.stroke();

    const midY = (topY + botY) / 2;
    arrowHead(ctx, midY > topY ? midY : topY + 1, topY, hLineX, topY, blue, 7, true);
    arrowHead(ctx, midY < botY ? midY : botY - 1, botY, hLineX, botY, blue, 7, true);

    ctx.font = '500 13px system-ui, sans-serif';
    ctx.fillStyle = textColor;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(`h = ${fmt(height)}${unit ? ' ' + unit : ''}`, hLineX + 7, midY);
}

function arrowHead(
    ctx: CanvasRenderingContext2D,
    fromAxisPos: number,
    toAxisPos: number,
    lineX: number,
    tipY: number,
    color: string,
    size: number,
    vertical = false,
): void {
    ctx.beginPath();
    if (vertical) {
        // Vertical arrow pointing to tipY; direction determined relative to the line's midpoint
        const dir = tipY < fromAxisPos ? -1 : 1;
        ctx.moveTo(lineX, tipY);
        ctx.lineTo(lineX - size / 2, tipY - dir * size);
        ctx.lineTo(lineX + size / 2, tipY - dir * size);
    } else {
        // Horizontal arrow pointing to (lineX, tipY) from right side (pointing left)
        const angle = Math.atan2(tipY - toAxisPos, lineX - fromAxisPos);
        ctx.moveTo(lineX, tipY);
        ctx.lineTo(
            lineX - size * Math.cos(angle - Math.PI / 6),
            tipY - size * Math.sin(angle - Math.PI / 6),
        );
        ctx.lineTo(
            lineX - size * Math.cos(angle + Math.PI / 6),
            tipY - size * Math.sin(angle + Math.PI / 6),
        );
    }
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
}

function shade(hex: string, factor: number): string {
    const cleaned = hex.replace('#', '');
    const r = parseInt(cleaned.substring(0, 2), 16);
    const g = parseInt(cleaned.substring(2, 4), 16);
    const b = parseInt(cleaned.substring(4, 6), 16);
    const c = (v: number) => Math.min(255, Math.max(0, Math.round(v * factor)));
    return `rgb(${c(r)}, ${c(g)}, ${c(b)})`;
}
